import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";

const PAGE_SIZE = 25;

interface Notification {
  notification_id: number;
  type: string;
  store_id: number | null;
  title: string;
  message: string;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

interface EditNotification {
  notification_id?: number;
  type: string;
  store_id: string;
  title: string;
  message: string;
  valid_from: string;
  valid_until: string;
}

interface Store {
  store_id: number;
  store_name: string;
}

const EMPTY_NOTIFICATION: EditNotification = {
  type: "promotion",
  store_id: "",
  title: "",
  message: "",
  valid_from: "",
  valid_until: "",
};

// Promotions and announcements only — this page manages the broadcast
// Notifications table. Order-lifecycle notifications (OrderNotifications)
// are entirely system-generated and never shown/edited here.
const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editNotification, setEditNotification] = useState<EditNotification>(EMPTY_NOTIFICATION);
  const [isLoading, setIsLoading] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/notification?${params.toString()}`, { method: 'GET' }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch notifications. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/store?page=1&limit=100`, { method: 'GET' }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setStores(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch stores. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    }
  }, [cookies.auth]);

  const handleAdd = () => {
    setEditNotification(EMPTY_NOTIFICATION);
    setShowAddModal(true);
  };

  const handleUpdate = () => {
    if (selectedNotifications.length === 0) {
      alert("Please select at least one notification to update");
      return;
    }

    const toEdit = notifications.find(n => n.notification_id === selectedNotifications[0]);
    if (toEdit) {
      setEditNotification({
        notification_id: toEdit.notification_id,
        type: toEdit.type,
        store_id: toEdit.store_id?.toString() || "",
        title: toEdit.title,
        message: toEdit.message,
        valid_from: toEdit.valid_from ? toEdit.valid_from.split('T')[0] : "",
        valid_until: toEdit.valid_until ? toEdit.valid_until.split('T')[0] : "",
      });
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedNotifications.length === 0) {
      alert("Please select at least one notification to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const notificationId of selectedNotifications) {
        const response = await authenticatedFetch(`${API_BASE}/notification/${notificationId}`, { method: "DELETE" }, cookies.auth);
        if (!response.ok) {
          console.error(`Failed to delete notification ${notificationId}. Status:`, response.status);
        }
      }
      setSelectedNotifications([]);
      setShowDeleteConfirm(false);
      await fetchNotifications();
    } catch (error) {
      console.error("Error deleting notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);

      if (!editNotification.title.trim()) {
        snack.onResponse({ message: "Title is required", status: 400 });
        return;
      }
      if (!editNotification.message.trim()) {
        snack.onResponse({ message: "Message is required", status: 400 });
        return;
      }
      if (editNotification.valid_from && editNotification.valid_until &&
          new Date(editNotification.valid_from) >= new Date(editNotification.valid_until)) {
        snack.onResponse({ message: "Valid until date must be after valid from date", status: 400 });
        return;
      }

      const payload: Record<string, unknown> = {
        type: editNotification.type,
        title: editNotification.title.trim(),
        message: editNotification.message.trim(),
        valid_from: editNotification.valid_from || null,
        valid_until: editNotification.valid_until || null,
      };
      if (editNotification.store_id) {
        payload.store_id = parseInt(editNotification.store_id);
      }

      const url = isEdit ? `${API_BASE}/notification/${editNotification.notification_id}` : `${API_BASE}/notification`;
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }, cookies.auth);

      if (response.ok) {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditNotification(EMPTY_NOTIFICATION);
        setSelectedNotifications([]);
        await fetchNotifications();
        snack.onResponse({ message: "Notification saved successfully!", status: 200 });
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save notification"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving notification:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save notification", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedNotifications(checked ? notifications.map(n => n.notification_id) : []);
  };

  const handleSelectNotification = (notificationId: number, checked: boolean) => {
    setSelectedNotifications(checked ? [notificationId] : selectedNotifications.filter(id => id !== notificationId));
  };

  const getStoreName = (storeId: number | null | undefined) => {
    if (!storeId) return "All Stores";
    const store = stores.find(s => s.store_id === storeId);
    return store ? store.store_name : "Unknown";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return "Invalid Date";
    }
  };

  const renderForm = () => (
    <>
      <div className={style.formGroup}>
        <label>Type:</label>
        <select
          value={editNotification.type}
          onChange={(e) => setEditNotification({ ...editNotification, type: e.target.value })}
          className={style.select}
        >
          <option value="promotion">Promotion</option>
          <option value="announcement">Announcement</option>
        </select>
      </div>
      <div className={style.formGroup}>
        <label>Store (optional — leave blank for all stores):</label>
        <select
          value={editNotification.store_id}
          onChange={(e) => setEditNotification({ ...editNotification, store_id: e.target.value })}
          className={style.select}
        >
          <option value="">All Stores</option>
          {stores.map(store => (
            <option key={store.store_id} value={store.store_id}>{store.store_name}</option>
          ))}
        </select>
      </div>
      <div className={style.formGroup}>
        <label>Title:</label>
        <input
          type="text"
          value={editNotification.title}
          onChange={(e) => setEditNotification({ ...editNotification, title: e.target.value })}
          className={style.input}
          placeholder="e.g. Eid Sale — 20% Off Everything"
        />
      </div>
      <div className={style.formGroup}>
        <label>Message:</label>
        <textarea
          value={editNotification.message}
          onChange={(e) => setEditNotification({ ...editNotification, message: e.target.value })}
          className={style.textarea}
          placeholder="e.g. We'll be closed from Aug 10 to Aug 12 for the holiday. Sorry for the inconvenience!"
        />
      </div>
      <div className={style.formGroup}>
        <label>Valid From (optional):</label>
        <input
          type="date"
          value={editNotification.valid_from}
          onChange={(e) => setEditNotification({ ...editNotification, valid_from: e.target.value })}
          className={style.input}
        />
      </div>
      <div className={style.formGroup}>
        <label>Valid Until (optional):</label>
        <input
          type="date"
          value={editNotification.valid_until}
          onChange={(e) => setEditNotification({ ...editNotification, valid_until: e.target.value })}
          className={style.input}
          min={editNotification.valid_from || undefined}
        />
      </div>
    </>
  );

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Notifications</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>Add</button>
              <button className={style.actionBtn} onClick={handleUpdate}>Update</button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>Delete</button>
            </div>
          )}
          {permissions.isSales && (
            <div className={style.viewOnlyNotice}>
              <span style={{ color: '#666', fontStyle: 'italic' }}>View-only access - Sales Person</span>
            </div>
          )}
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedNotifications.length === notifications.length && notifications.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Type</th>
              <th>Store</th>
              <th>Title</th>
              <th>Message</th>
              <th>Valid</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {notifications.map((notification) => (
              <tr key={notification.notification_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedNotifications.includes(notification.notification_id)}
                      onChange={(e) => handleSelectNotification(notification.notification_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td style={{ textTransform: 'capitalize' }}>{notification.type}</td>
                <td>{getStoreName(notification.store_id)}</td>
                <td>{notification.title}</td>
                <td className={style.descriptionCell}>{notification.message}</td>
                <td>
                  {notification.valid_from || notification.valid_until
                    ? `${formatDate(notification.valid_from)} — ${formatDate(notification.valid_until)}`
                    : 'Always'}
                </td>
                <td>{formatDate(notification.created_at)}</td>
              </tr>
            ))}
            {notifications.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: "20px", color: "#666" }}>No notifications found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {showAddModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Add Notification</h2>
            {renderForm()}
            <div className={style.modalActions}>
              <button onClick={() => setShowAddModal(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={() => handleSave(false)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Edit Notification</h2>
            {renderForm()}
            <div className={style.modalActions}>
              <button onClick={() => setShowEditModal(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={() => handleSave(true)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected notifications?</p>
            <div className={style.modalActions}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn}>Cancel</button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
